import { QRCodeSVG } from "qrcode.react";
import { createPortal } from "react-dom";
import { X, Printer, ShieldCheck, Clock } from "lucide-react";
import { formatEU } from "./public-book/formatters";

/**
 * DigitalRentalPass (v4.0.5) — "Digital Key Pass" card carrying a QR code
 * that deep-links the guest straight into `/manage-booking`. Rendered both in
 * the post-reservation confirmation screen and from the self-service portal.
 * Frontend-only: everything is derived from the booking object already loaded.
 * v4.7.4 — restored the light luxury theme (white card, dark type).
 */
export default function DigitalRentalPass({
  booking,
  companyName = "",
  onClose,
  embedded = false,
}) {
  const reference = (booking?.booking_reference || booking?.id || "").slice(0, 8).toUpperCase();
  const email = booking?.customer_email || booking?.email || booking?.user_email || "";
  const passUrl = `${window.location.origin}/manage-booking?ref=${reference}&email=${encodeURIComponent(email)}`;

  const ready = Boolean(
    booking?.driver_verification?.verified_at
    || (String(booking?.id_passport || "").trim() && String(booking?.license_number || "").trim()),
  );

  // v4.7.4 — the check-in badge belongs to live reservations only: hide it once
  // the rental is over, handed back, cancelled or past its drop-off time.
  const dropoff = booking?.return_date
    ? new Date(`${booking.return_date}T${(booking.return_time || "23:59").slice(0, 5)}:00`)
    : null;
  const liveReservation =
    booking?.status === "confirmed" && (!dropoff || new Date() <= dropoff);

  const vehicleName =
    booking?.vehicle_snapshot?.name || booking?.vehicle_name || booking?.vehicle_snapshot?.brand || "Vehicle";

  return (
    <div
      className="bg-white text-neutral-900 rounded-2xl overflow-hidden shadow-2xl ring-1 ring-black/5 w-full max-w-md mx-auto key-pass-card"
      id="key-pass-card"
      data-testid="digital-rental-pass"
    >
      <div className="px-6 pt-6 pb-5 border-b border-dashed border-neutral-300 bg-[#F8FAFC]">
        <p className="text-[10px] uppercase tracking-[0.35em] text-neutral-500">
          {companyName || "Car Rental App"}
        </p>
        <h3 className="font-display text-2xl mt-1.5 leading-tight">Digital Key Pass</h3>

        <div className="mt-5 space-y-3 text-left">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-neutral-500">Booking reference</p>
            <p className="font-mono text-lg mt-0.5" data-testid="pass-reference">{reference}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-neutral-500">Vehicle</p>
            <p className="text-base mt-0.5" data-testid="pass-vehicle">{vehicleName}</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-[10px] uppercase tracking-[0.28em] text-neutral-500">Pickup</p>
              <p className="text-sm mt-0.5 tabular-nums">
                {formatEU(booking?.pickup_date)} {booking?.pickup_time || ""}
              </p>
              <p className="text-xs text-neutral-500">{booking?.pickup_location || "—"}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.28em] text-neutral-500">Return</p>
              <p className="text-sm mt-0.5 tabular-nums">
                {formatEU(booking?.return_date)} {booking?.return_time || ""}
              </p>
              <p className="text-xs text-neutral-500">{booking?.return_location || "—"}</p>
            </div>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-neutral-500">Driver</p>
            <p className="text-sm mt-0.5" data-testid="pass-driver">{booking?.full_name || email || "—"}</p>
          </div>
        </div>
      </div>

      <div className="px-6 py-6 flex flex-col items-center">
        <div className="bg-white rounded-xl p-4 ring-1 ring-neutral-200" data-testid="pass-qr">
          <QRCodeSVG value={passUrl} size={148} level="M" includeMargin={false} />
        </div>
        <p className="text-xs text-neutral-500 mt-3">Scan at Pickup / Handover Desk</p>

        {liveReservation && (
          <span
            className={`mt-4 inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full ${
              ready ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"
            }`}
            data-testid="pass-status-badge"
          >
            {ready ? <ShieldCheck size={13} /> : <Clock size={13} />}
            {ready ? "Ready to Drive" : "Pending Desk Check-In"}
          </span>
        )}
      </div>

      <div className="px-6 pb-6 flex gap-2 print:hidden">
        <button
          type="button"
          onClick={() => {
            const clear = () => document.body.classList.remove("printing-key-pass");
            document.body.classList.add("printing-key-pass");
            window.addEventListener("afterprint", clear, { once: true });
            window.print();
            setTimeout(clear, 4000);
          }}
          className="flex-1 inline-flex items-center justify-center gap-2 bg-neutral-900 text-white text-sm font-semibold py-2.5 rounded-lg hover:bg-neutral-800 transition-colors"
          data-testid="pass-print-btn"
        >
          <Printer size={15} /> Print / Save Key Pass
        </button>
        {!embedded && (
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center gap-2 border border-neutral-300 text-sm px-4 py-2.5 rounded-lg hover:bg-neutral-100 transition-colors"
            data-testid="pass-close-btn"
          >
            <X size={15} /> Close
          </button>
        )}
      </div>
    </div>
  );
}

/** Modal wrapper used by `/manage-booking`. Rendered through a portal so the
 *  print stylesheet can isolate it as the only child of <body> (1-page pass). */
export function DigitalRentalPassModal({ booking, companyName, onClose }) {
  return createPortal(
    <div
      className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto key-pass-overlay"
      onClick={onClose}
      data-testid="digital-pass-modal"
    >
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md my-auto">
        <DigitalRentalPass booking={booking} companyName={companyName} onClose={onClose} />
      </div>
    </div>,
    document.body,
  );
}
